import {
  getPersonaSnapshot,
  normalizeAnchorsData,
} from '../../../Persona/Audience/AudienceInsights/audienceInsightsHelper';
import { compactNumber } from '../utils/formatNumbers';

/**
 * View model of the Segment Details page, built from one report segment (`respData` of
 * GET /audiences/{id}/segments — the same payload the legacy segment page renders; field mapping as
 * in Persona/Audience/AudienceInsights). Pure: no fetching. Missing data → null / [] so each card can
 * hide itself.
 */

const percent = (weight) => Math.round((Number(weight) || 0) * 100);

/** "$50,000-$75,000" → "$50–75K"; "$200,000+" → "$200K+"; "25-34" → "25–34". */
export const formatBucket = (bucket) => {
  const text = String(bucket ?? '');
  const money = text.match(/^\$?([\d,]+)\s*-\s*\$?([\d,]+)$/);
  if (money && text.includes('$')) {
    const [low, high] = [money[1], money[2]].map((n) => Number(n.replace(/,/g, '')));
    return `$${compactNumber(low).replace(/K$/, '')}–${compactNumber(high)}`;
  }
  const open = text.match(/^\$([\d,]+)\+$/);
  if (open) return `$${compactNumber(Number(open[1].replace(/,/g, '')))}+`;
  return text.replace(/(\d)\s*-\s*(\d)/, '$1–$2');
};

/** Age: the strongest pair of adjacent buckets ("25–44"), plus the single strongest ("core"). */
const ageSummary = (age = []) => {
  const buckets = age.filter((item) => item?.bucket);
  if (!buckets.length) return null;
  let bestPair = null;
  for (let i = 0; i < buckets.length - 1; i += 1) {
    const weight = (buckets[i].weight || 0) + (buckets[i + 1].weight || 0);
    if (!bestPair || weight > bestPair.weight) bestPair = { index: i, weight };
  }
  const core = buckets.reduce((max, item) => ((item.weight || 0) > (max.weight || 0) ? item : max));
  if (!bestPair || (core.weight || 0) >= bestPair.weight) return formatBucket(core.bucket);
  const low = String(buckets[bestPair.index].bucket).split('-')[0];
  const highBucket = String(buckets[bestPair.index + 1].bucket);
  const high = highBucket.includes('-') ? highBucket.split('-')[1] : highBucket;
  return `${low}–${high} · core ${formatBucket(core.bucket)}`;
};

// Benchmarks: lower is better for cost metrics.
const LOWER_IS_BETTER = /cost|cpa|cpc|cpm/i;
const benchmarkTag = (metric, delta) => {
  const value = parseFloat(String(delta).replace('−', '-'));
  if (!Number.isFinite(value) || value === 0) return null;
  if (LOWER_IS_BETTER.test(metric)) {
    return value < 0
      ? { label: 'Better than baseline', tone: 'good' }
      : { label: 'Worse than baseline', tone: 'bad' };
  }
  return value > 0
    ? { label: 'Above baseline', tone: 'good' }
    : { label: 'Below baseline', tone: 'bad' };
};

// "Click-Through Rate (CTR)" → "Click-through rate"; keeps other parentheses ("Engagement (Reels/UGC)").
const metricLabel = (metric = '') => {
  const plain = String(metric).replace(/\s*\((CTR|CPA|LTV|CPC|CPM)\)\s*$/i, '');
  return (
    plain.charAt(0) +
    plain
      .slice(1)
      .toLowerCase()
      .replace(/ugc/g, 'UGC')
      .replace(/reels/g, 'Reels')
  );
};

const OBJECTIVE_TAG = {
  awareness: 'Awareness audience',
  consideration: 'Consideration audience',
  conversion: 'Conversion audience',
  'lead gen': 'Lead gen audience',
  leadgen: 'Lead gen audience',
};

const asStrings = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => (typeof item === 'string' ? item : null))
    .filter(Boolean);

/** Bars of a distribution ({ label, value% , width% of the largest }) in the given order. */
const bars = (items = [], labelKey, { sort = false } = {}) => {
  const list = items
    .filter((item) => item && item[labelKey] != null)
    .map((item) => ({ label: formatBucket(item[labelKey]), value: percent(item.weight) }));
  const ordered = sort ? [...list].sort((a, b) => b.value - a.value) : list;
  const max = Math.max(0, ...ordered.map((item) => item.value));
  return ordered.map((item) => ({ ...item, width: max ? (item.value / max) * 100 : 0 }));
};

export function buildSegmentDetails(segment) {
  const resp = segment?.respData || {};
  const prompt = resp.promptDetails || {};
  const demographics = resp.demographicsV3 || {};
  const snapshot = getPersonaSnapshot(resp);

  const estimate = resp.segmentSize?.absolute_estimate;
  const size =
    estimate?.low != null && estimate?.high != null
      ? `${compactNumber(estimate.low)}–${compactNumber(estimate.high)}`
      : null;

  const genderTotal = (snapshot.gender?.female || 0) + (snapshot.gender?.male || 0);
  const gender =
    snapshot.gender && genderTotal
      ? {
          female: Math.round(snapshot.gender.female),
          male: Math.round(snapshot.gender.male),
          other: Math.max(
            0,
            100 - Math.round(snapshot.gender.female) - Math.round(snapshot.gender.male)
          ),
        }
      : null;

  const education = demographics.education?.length
    ? (() => {
        const top = demographics.education.reduce((max, item) =>
          (item.weight || 0) > (max.weight || 0) ? item : max
        );
        return `${top.label} ${percent(top.weight)}%`;
      })()
    : null;

  const benchmarks = (Array.isArray(prompt.predictive_performance_benchmark)
    ? prompt.predictive_performance_benchmark
    : []
  )
    .filter((item) => item?.metric)
    .map((item) => ({
      metric: metricLabel(item.metric),
      delta: String(item.delta_percent ?? '').replace(/^-/, '−'),
      tag: benchmarkTag(item.metric, item.delta_percent),
      note: item.confidence_message || null,
    }));

  const messaging = prompt.campaign_messaging || {};
  const recommendation = prompt.system_recommendation || {};
  const objective = String(resp.user_input?.objective || '').toLowerCase();

  const signals = (Array.isArray(prompt.behavioral_signals) ? prompt.behavioral_signals : [])
    .filter((item) => item?.behavior)
    .map((item) => ({
      behavior: item.behavior,
      notes: item.notes || null,
      index: Number(item.engagement_index) || 0,
    }));
  const maxIndex = Math.max(0, ...signals.map((item) => item.index));

  const ageBars = bars(demographics.age, 'bucket');
  const largestAge = ageBars.reduce(
    (max, item) => (!max || item.value > max.value ? item : max),
    null
  );

  return {
    overview: {
      image: resp.imageS3Path || segment?.thumbnail || null,
      quote: prompt.quote || null,
      description:
        resp.summary?.textGenerate || resp.outline?.description || segment?.description || null,
      stats: {
        size,
        sizeNote: resp.segmentSize?.rationale || null,
        age: ageSummary(demographics.age),
        gender,
        income: snapshot.income ? formatBucket(snapshot.income) : null,
        education,
        // The snapshot's locations are weight-ordered; the Overview shows only the top one.
        location: snapshot.locations?.[0] || null,
      },
    },
    benchmarks,
    interests: asStrings(resp.primaryInterests),
    hashtags: asStrings(prompt.hashtags),
    coreInsight: prompt.core_insight || null,
    painPoints: asStrings(prompt.pain_points),
    messaging:
      messaging.hero_line || messaging.support_copy
        ? { headline: messaging.hero_line || null, body: messaging.support_copy || null }
        : null,
    motivations: (Array.isArray(prompt.buying_motivations) ? prompt.buying_motivations : [])
      .filter((item) => item?.motivation)
      .map((item) => ({ title: item.motivation, description: item.description || null })),
    recommendation: prompt.summary_opportunity
      ? { text: prompt.summary_opportunity, tag: OBJECTIVE_TAG[objective] || null }
      : null,
    mediaHooks: asStrings(recommendation.automated_output?.ad_copy),
    strategies: (Array.isArray(messaging.creative_directions) ? messaging.creative_directions : [])
      .filter((item) => item && typeof item === 'object' && item.platform_name)
      .map((item) => ({
        platform: item.platform_name,
        content: item.example_content || null,
        objective: item.objective || null,
      })),
    anchors: normalizeAnchorsData(resp.anchorsMap)
      .filter((item) => item.anchor_label)
      .map((item) => ({
        label: item.anchor_label,
        description: item.anchor_description || null,
        score: item.normalized_score,
      })),
    signals: signals.map((item) => ({
      ...item,
      width: maxIndex ? (item.index / maxIndex) * 100 : 0,
    })),
    demographics: {
      age: ageBars,
      largestAge,
      ethnicity: bars(demographics.ethnicity, 'label', { sort: true }),
      education: bars(demographics.education, 'label', { sort: true }),
      income: bars(demographics.income, 'bucket'),
    },
  };
}

/** Sections that have something to show, in page order (drives the sections and the nav rail). */
export const DETAIL_SECTIONS = [
  { key: 'overview', id: 'segment-overview', label: 'Overview' },
  { key: 'core-findings', id: 'segment-core-findings', label: 'Core findings' },
  { key: 'recommendations', id: 'segment-recommendations', label: 'Recommendations' },
  { key: 'behaviour', id: 'segment-behaviour', label: 'Behaviour' },
  { key: 'demographics', id: 'segment-demographics', label: 'Demographics' },
];

export const sectionHasData = (key, details) => {
  switch (key) {
    case 'overview':
      return true;
    case 'core-findings':
      return Boolean(
        details.coreInsight ||
          details.painPoints.length ||
          details.messaging ||
          details.motivations.length
      );
    case 'recommendations':
      return Boolean(
        details.recommendation || details.mediaHooks.length || details.strategies.length
      );
    case 'behaviour':
      return Boolean(details.anchors.length || details.signals.length);
    case 'demographics':
      return Boolean(
        details.demographics.age.length ||
          details.demographics.ethnicity.length ||
          details.demographics.education.length ||
          details.demographics.income.length
      );
    default:
      return false;
  }
};
